import { Component, Input } from '@angular/core';

@Component({
  selector: 'app-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  // `aria-label` and `aria-describedby` are aliased to inputs below so they
  // reach the native button. Angular applies a *static* attribute to the host as
  // well, and this host has no role to carry it, so `aria-label` is cleared
  // here. A caller that needs a dynamic name binds `[aria-label]` (the input):
  // `[attr.aria-label]` would target this same host attribute and collide with
  // the clearing.
  //
  // **`aria-describedby` is deliberately NOT cleared, and the asymmetry is the
  // point.** `[appTooltip]` describes its host through the CDK's
  // `AriaDescriber`, from `ngOnInit` -- and a host binding runs after the
  // declaring view's template, so clearing it wiped what the CDK had just
  // written and never rewrote it, since the bound value never changes again.
  // Clearing it also protected nothing: no call site passes a *static*
  // `aria-describedby`, and the ones that pass a value use a property binding,
  // which never stamps the host.
  //
  // What it does **not** do is fix an accessibility defect, and the distinction
  // is worth keeping straight: the attribute lands on this host, which has no
  // role and no tabindex, so no assistive tech reads it -- for the blocked
  // buttons the native `<button>` is `disabled` and unfocusable besides. Not
  // clearing it restores the status quo at zero risk; it does not make the
  // tooltip's sentence reachable. The pattern that does is the one
  // `status-actions.component.html` uses in this same delivery: forward
  // `[aria-describedby]` to the native button and point it at a
  // `cdk-visually-hidden` node. Four templates still put `appTooltip` straight
  // on an `<app-button>` and need that treatment -- recorded on the card.
  host: {
    '[class]': 'classes',
    '[attr.aria-label]': 'null',
  },
})
export class ButtonComponent {
  /**
   * Aliased to the attribute name, so a plain `aria-label` reaches the native button;
   * `[attr.aria-label]` would reach only the host. Icon-only buttons only: it replaces
   * the accessible name, and dropping the visible words breaks speech input.
   */
  @Input('aria-label') ariaLabel?: string;
  /** Same reasoning, for the explanation of a disabled control. */
  @Input('aria-describedby') ariaDescribedby?: string;
  @Input() type: ButtonType = 'button';
  @Input() disabled = false;
  @Input() variant: (typeof variants)[number] = 'primary';
  @Input() size: (typeof sizes)[number] = 'default';
  @Input() classButton = '';
  @Input() isLoading = false;

  get classes(): string {
    const classList: string[] = ['button'];
    classList.push(`variant-${this.variant}`);
    classList.push(`size-${this.size}`);
    return classList.join(' ');
  }
}

type ButtonType = 'button' | 'submit';

const variants = [
  'primary',
  'secondary',
  'ghost',
  'ghost-primary',
  'white',
  'white-full',
  'white-outline',
  'white-primary',
  'success',
  'ghost-destructive',
  'destructive',
  'destructive-solid',
  'none',
  'gray',
  'neutral',
  'ghost-secondary',
  'financial-blue',
  'financial-white',
] as const;

const sizes = [
  'default',
  'icon',
  'small',
  'default-none',
  'circle',
  'circle-small',
  'none',
] as const;
